import { lazy, Suspense, useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
  Plus,
  ArrowRight,
  Sparkles,
  Pin,
  ChevronLeft,
  ChevronRight,
  Upload,
  Mic,
  X,
  Link as LinkIcon,
  Globe,
  Lock,
  Brain,
  FolderPlus,
  Trash2,
} from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { useNavigate } from "react-router";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import { DndProvider, useDrag, useDrop, useDragLayer } from "react-dnd";
import { HTML5Backend, getEmptyImage } from "react-dnd-html5-backend";
import { TouchBackend } from "react-dnd-touch-backend";
import { ThoughtCard, type Thought } from "./ThoughtCard";
import { StatsBar } from "./StatsBar";
import { CardDetailSheet } from "./CardDetailSheet";
import { CollectionPicker } from "./CollectionPicker";
// Lazy so the Collections page chunk stays out of the main bundle — the modal
// only mounts when the user opens "create collection" from Home.
const CreateCollectionModal = lazy(() =>
  import("./CollectionsPage").then((m) => ({ default: m.CreateCollectionModal })),
);
import { useBurrow } from "./BurrowStore";
import { useAuth } from "../lib/auth/AuthProvider";
import { useResurface, COOLDOWN_MS } from "../lib/useResurface";
import type { Collection } from "./CollectionCard";
import { useSignedObjectUrl } from "../lib/useSignedObjectUrl";

const DRAG_TYPE = "THOUGHT_CARD";

interface DragItem {
  type: typeof DRAG_TYPE;
  thoughtId: string;
}

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return "Good Morning";
  if (hour < 17) return "Good Afternoon";
  return "Good Evening";
}

/* ─── Page-level drop zone (prevents "no" cursor everywhere) ─── */
function PageDropZone({ children }: { children: React.ReactNode }) {
  const [, dropRef] = useDrop(() => ({
    accept: DRAG_TYPE,
    drop: (_item: DragItem, monitor) => {
      // Only consume if no deeper target handled it
      if (monitor.didDrop()) return;
      // Dropped on the page background — do nothing
    },
  }), []);

  return (
    <div ref={dropRef as unknown as React.Ref<HTMLDivElement>}>
      {children}
    </div>
  );
}

/* ─── Custom Drag Layer ─── */
function CustomDragLayer({ thoughts }: { thoughts: Thought[] }) {
  const { isDragging, item, currentOffset } = useDragLayer((monitor) => ({
    isDragging: monitor.isDragging(),
    item: monitor.getItem() as DragItem | null,
    currentOffset: monitor.getClientOffset(),
  }));

  if (!isDragging || !currentOffset || !item) return null;

  const thought = thoughts.find((t) => t.id === item.thoughtId);
  if (!thought) return null;

  return (
    <div
      className="fixed inset-0 pointer-events-none"
      style={{ zIndex: 9999 }}
    >
      <div
        style={{
          transform: `translate(${currentOffset.x - 100}px, ${currentOffset.y - 24}px)`,
        }}
      >
        <div
          className="w-[200px] rounded-2xl bg-card/95 backdrop-blur-xl border border-accent/30 shadow-2xl shadow-accent/15 px-4 py-3 rotate-[2deg]"
          style={{ transition: "box-shadow 0.2s" }}
        >
          {thought.title && (
            <p
              className="text-primary truncate mb-0.5"
              style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: "0.82rem" }}
            >
              {thought.title}
            </p>
          )}
          <p className="text-foreground/60 line-clamp-2" style={{ fontSize: "0.75rem", lineHeight: "1.5" }}>
            {thought.body}
          </p>
        </div>
      </div>
    </div>
  );
}

/* ─── Draggable Thought Wrapper ─── */
function DraggableThought({
  thought,
  index,
  onClick,
  onDelete,
  pinned,
  onTogglePin,
}: {
  thought: Thought;
  index: number;
  onClick: () => void;
  onDelete: (id: string) => void;
  pinned?: boolean;
  onTogglePin?: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const [{ isDragging }, dragRef, previewRef] = useDrag(
    () => ({
      type: DRAG_TYPE,
      item: { type: DRAG_TYPE, thoughtId: thought.id } as DragItem,
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    }),
    [thought.id]
  );

  // Hide the default browser drag ghost
  useEffect(() => {
    previewRef(getEmptyImage(), { captureDraggingState: true });
  }, [previewRef]);

  return (
    <div
      ref={dragRef as unknown as React.Ref<HTMLDivElement>}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="cursor-grab active:cursor-grabbing w-full relative"
      style={{
        opacity: isDragging ? 0.25 : 1,
        transform: isDragging ? "scale(0.97)" : "scale(1)",
        transition: "opacity 0.25s ease, transform 0.25s ease",
      }}
    >
      <ThoughtCard thought={thought} index={index} />
      {/* Top-right action buttons */}
      <div className={`absolute top-2 right-2 z-10 flex items-center gap-1 transition-opacity duration-200 ${
        hovered || pinned ? "opacity-100" : "opacity-0"
      }`}>
        {onTogglePin && (
          <button
            onClick={(e) => { e.stopPropagation(); onTogglePin(); }}
            className={`w-6 h-6 flex items-center justify-center rounded-lg transition-all duration-200 ${
              pinned
                ? "bg-accent text-accent-foreground shadow-sm shadow-accent/20"
                : "bg-card/90 border border-border/40 text-muted-foreground/40 hover:text-accent hover:border-accent/30"
            }`}
            title={pinned ? "Unpin" : "Pin to keep"}
          >
            <Pin size={11} className={pinned ? "fill-current" : ""} style={pinned ? { transform: "rotate(-45deg)" } : undefined} />
          </button>
        )}
        {hovered && !isDragging && (
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(thought.id); }}
            className="w-6 h-6 flex items-center justify-center rounded-lg bg-card/90 border border-border/40 text-muted-foreground hover:text-destructive hover:border-destructive/40 transition-colors shadow-sm"
            title="Delete"
          >
            <Trash2 size={11} />
          </button>
        )}
      </div>
    </div>
  );
}

/* ─── Inline Drop Card ─── */
function DropCard({
  onSave,
  onUploadImage,
  triggerOpen,
}: {
  onSave: (payload: { title?: string; body: string }) => void;
  onUploadImage?: (file: File, body?: string) => void;
  triggerOpen?: number;
}) {
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [isDragOver, setIsDragOver] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const [detectedUrl, setDetectedUrl] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [interimText, setInterimText] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const titleRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const recognitionRef = useRef<any>(null);
  const textBeforeRecordingRef = useRef("");
  const { showToast } = useBurrow();

  // External trigger to open
  useEffect(() => {
    if (triggerOpen && triggerOpen > 0) {
      setIsOpen(true);
      setTimeout(() => textareaRef.current?.focus(), 50);
    }
  }, [triggerOpen]);

  const urlPattern = /^https?:\/\/[^\s]+$/;

  const handleSave = () => {
    if (!text.trim() && !title.trim()) return;
    onSave({ title: title.trim() || undefined, body: text.trim() });
    setTitle("");
    setText("");
    setDetectedUrl(null);
    setShowSaved(true);
    setTimeout(() => setShowSaved(false), 2000);
  };

  const handleTextChange = (value: string) => {
    setText(value);
    if (urlPattern.test(value.trim())) {
      setDetectedUrl(value.trim());
    } else {
      setDetectedUrl(null);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const pasted = e.clipboardData.getData("text");
    if (urlPattern.test(pasted.trim())) {
      setDetectedUrl(pasted.trim());
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSave();
    }
    if (e.key === "Escape") {
      setTitle("");
      setText("");
      setDetectedUrl(null);
      setIsOpen(false);
      textareaRef.current?.blur();
    }
  };

  // Only handle external drops (files/images from outside the browser).
  // Internal react-dnd drags (card → collection) must pass through unblocked.
  const hasFiles = (e: React.DragEvent) =>
    Array.from(e.dataTransfer.types).includes("Files");

  const handleDrop = (e: React.DragEvent) => {
    if (!hasFiles(e)) { setIsDragOver(false); return; }
    e.preventDefault();
    setIsDragOver(false);
    const droppedText = e.dataTransfer.getData("text/plain");
    if (droppedText) {
      setText(droppedText);
      if (urlPattern.test(droppedText.trim())) {
        setDetectedUrl(droppedText.trim());
      }
    }
    const images = Array.from(e.dataTransfer.files).filter((f) => f.type.startsWith("image/"));
    if (images.length > 0) {
      const body = text.trim() || undefined;
      images.forEach((f) => onUploadImage?.(f, body));
      setText("");
      setDetectedUrl(null);
      setShowSaved(true);
      setTimeout(() => setShowSaved(false), 2000);
    }
  };

  const isActive = isOpen;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      onClick={() => {
        if (!isActive) {
          setIsOpen(true);
          setTimeout(() => textareaRef.current?.focus(), 50);
        }
      }}
      className={`relative rounded-2xl cursor-text h-full min-h-[180px] w-full ${
        isDragOver ? "ring-2 ring-accent/30 scale-[1.005]" : ""
      }`}
      layout={false}
      style={{
        background: isActive
          ? "var(--card)"
          : "linear-gradient(145deg, color-mix(in srgb, var(--accent) 4%, var(--card)) 0%, var(--card) 100%)",
        border: isActive
          ? "1.5px solid color-mix(in srgb, var(--accent) 25%, transparent)"
          : "1.5px dashed color-mix(in srgb, var(--accent) 15%, transparent)",
        boxShadow: isActive
          ? "0 0 0 3px color-mix(in srgb, var(--accent) 6%, transparent), 0 2px 12px rgba(0,0,0,0.04)"
          : "none",
        transition: "background 0.3s, border-color 0.3s, box-shadow 0.3s",
      }}
      onDragOver={(e) => {
        if (!hasFiles(e)) return; // Only intercept file drops, not react-dnd card drags
        e.preventDefault();
        setIsDragOver(true);
      }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={handleDrop}
    >
      {/* Collapsed idle state */}
      {!isActive && (
        <div className="flex h-full items-center gap-3 px-6 py-6">
          <div
            className="rounded-xl flex items-center justify-center shrink-0 w-9 h-9"
            style={{ background: "color-mix(in srgb, var(--accent) 10%, transparent)" }}
          >
            <Plus size={18} className="text-accent/60" />
          </div>
          <div className="flex flex-col gap-0.5 min-w-0">
            <span
              className="text-foreground/45"
              style={{ fontSize: "0.9rem", fontFamily: "'Fraunces', Georgia, serif" }}
            >
              Drop a thought…
            </span>
            <span className="text-muted-foreground/30" style={{ fontSize: "0.7rem" }}>
              <span className="hint-keyboard">
                Click, press{" "}
                <kbd
                  className="px-1.5 py-px rounded bg-muted/40 text-muted-foreground/40 mx-0.5"
                  style={{ fontSize: "0.68rem" }}
                >
                  N
                </kbd>{" "}
                or drag something in
              </span>
              <span className="hint-touch">Tap to start writing</span>
            </span>
          </div>
        </div>
      )}

      {/* Expanded active state */}
      {isActive && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.15 }}
          className="px-5 py-4 flex flex-col h-full"
        >
          {/* Header — the title IS the input: click "Untitled" and type */}
          <div className="flex items-center gap-2 mb-2">
            <div
              className="w-5 h-5 rounded-md flex items-center justify-center shrink-0"
              style={{ background: "color-mix(in srgb, var(--accent) 10%, transparent)" }}
            >
              <Plus size={11} className="text-accent/50" />
            </div>
            <input
              ref={titleRef}
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === "ArrowDown") {
                  e.preventDefault();
                  textareaRef.current?.focus();
                }
              }}
              placeholder="Untitled"
              className="flex-1 min-w-0 bg-transparent outline-none text-foreground placeholder:text-muted-foreground/40 placeholder:italic"
              style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: "0.9rem" }}
            />
            <AnimatePresence>
              {showSaved && (
                <motion.span
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0 }}
                  className="text-accent flex items-center gap-1 ml-auto shrink-0"
                  style={{ fontSize: "0.72rem" }}
                >
                  <Sparkles size={11} /> Kept
                </motion.span>
              )}
            </AnimatePresence>
          </div>

          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => handleTextChange(e.target.value)}
            onPaste={handlePaste}
            onKeyDown={handleKeyDown}
            placeholder={isRecording ? "Listening..." : "What's on your mind?"}
            className="w-full flex-1 min-h-0 bg-transparent resize-none outline-none text-foreground placeholder:text-muted-foreground/30 transition-colors"
            style={{
              fontFamily: "'Fraunces', Georgia, serif",
              fontSize: "0.94rem",
              lineHeight: "1.65",
            }}
          />

          {/* Live interim speech preview */}
          {isRecording && interimText && (
            <p
              className="text-accent/50 -mt-1 mb-1"
              style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: "0.88rem", lineHeight: "1.6", fontStyle: "italic" }}
            >
              {interimText}
            </p>
          )}

          {/* URL detection */}
          <AnimatePresence>
            {detectedUrl && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                className="flex items-center gap-2 mt-1 mb-2 px-2.5 py-1.5 rounded-lg bg-accent/8 border border-accent/15"
              >
                <LinkIcon size={11} className="text-accent shrink-0" />
                <span className="text-accent/80 truncate" style={{ fontSize: "0.72rem" }}>
                  {new URL(detectedUrl).hostname}
                </span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Action bar */}
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.15, delay: 0.05 }}
            className="flex items-center justify-between mt-2 pt-2 border-t border-border/15"
          >
            <div className="flex items-center gap-1.5">
              <button
                onClick={handleSave}
                disabled={!text.trim()}
                className="px-3 py-1 rounded-lg bg-accent text-accent-foreground transition-all hover:opacity-90 disabled:opacity-25 disabled:cursor-not-allowed"
                style={{ fontSize: "0.75rem" }}
              >
                Keep
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="p-1.5 rounded-lg text-muted-foreground/35 hover:text-foreground/60 hover:bg-muted/30 transition-all"
                title="Upload"
              >
                <Upload size={12} />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => {
                  const images = Array.from(e.target.files ?? []).filter((f) => f.type.startsWith("image/"));
                  if (images.length === 0) return;
                  // One card per photo; the composer text is shared as a caption.
                  const body = text.trim() || undefined;
                  images.forEach((f) => onUploadImage?.(f, body));
                  setText("");
                  setDetectedUrl(null);
                  setShowSaved(true);
                  setTimeout(() => setShowSaved(false), 2000);
                  e.currentTarget.value = "";
                }}
              />
            </div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => {
                  const SpeechRecognitionAPI =
                    (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
                  if (!SpeechRecognitionAPI) {
                    alert("Voice input is not supported in this browser. Try Chrome.");
                    return;
                  }
                  if (isRecording) {
                    recognitionRef.current?.stop();
                    setIsRecording(false);
                    setInterimText("");
                    return;
                  }
                  // Open the card and remember current text
                  if (!isOpen) setIsOpen(true);
                  textBeforeRecordingRef.current = text;

                  const recognition: any = new SpeechRecognitionAPI();
                  recognition.continuous = true;
                  recognition.interimResults = true;
                  recognition.lang = "en-US";
                  recognition.onresult = (e: any) => {
                    // Append ONLY newly-finalized results (tracked via
                    // e.resultIndex) to the running text, so a phrase is added
                    // exactly once and never repeats; interim words preview live
                    // in setInterimText until they finalize.
                    let interim = "";
                    for (let i = e.resultIndex; i < e.results.length; i++) {
                      const transcript = e.results[i][0].transcript;
                      if (e.results[i].isFinal) {
                        textBeforeRecordingRef.current +=
                          (textBeforeRecordingRef.current ? " " : "") + transcript.trim();
                      } else {
                        interim += transcript;
                      }
                    }
                    setText(textBeforeRecordingRef.current);
                    setInterimText(interim);
                  };
                  recognition.onend = () => {
                    setIsRecording(false);
                    setInterimText("");
                  };
                  recognition.onerror = (ev: any) => {
                    setIsRecording(false);
                    setInterimText("");
                    const msg =
                      ev.error === "not-allowed"
                        ? "Microphone permission denied."
                        : ev.error === "no-speech"
                        ? "No speech detected. Try again."
                        : "Voice input failed. Try again.";
                    showToast(msg);
                  };
                  recognitionRef.current = recognition;
                  recognition.start();
                  setIsRecording(true);
                }}
                className={`p-2 rounded-full transition-all ${
                  isRecording
                    ? "bg-accent text-accent-foreground shadow-lg shadow-accent/20 animate-pulse"
                    : "text-muted-foreground/40 hover:text-accent hover:bg-accent/10"
                }`}
                title={isRecording ? "Stop recording" : "Voice input"}
              >
                <Mic size={14} />
              </button>
              {text && (
                <button
                  onClick={() => {
                    setText("");
                    setDetectedUrl(null);
                  }}
                  className="text-muted-foreground/30 hover:text-foreground/50 transition-colors"
                >
                  <X size={12} />
                </button>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </motion.div>
  );
}

/* ─── Droppable Collection Card ─── */
function DroppableCollectionCard({
  collection,
  index,
  onClick,
  onDrop,
}: {
  collection: Collection;
  index: number;
  onClick: () => void;
  onDrop: (thoughtId: string, collectionId: string) => void;
}) {
  const [{ isOver, canDrop }, dropRef] = useDrop(
    () => ({
      accept: DRAG_TYPE,
      drop: (item: DragItem, monitor) => {
        if (monitor.didDrop()) return; // Already handled by a deeper target
        onDrop(item.thoughtId, collection.id);
      },
      collect: (monitor) => ({
        isOver: monitor.isOver({ shallow: true }),
        canDrop: monitor.canDrop(),
      }),
    }),
    [collection.id, onDrop]
  );

  const isActive = isOver && canDrop;

  const signed = useSignedObjectUrl({ bucket: "thought-images", path: collection.cover_image_path });
  const coverUrl = collection.cover_image_path && signed.status === "ready" ? signed.url : null;

  return (
    <motion.div
      ref={dropRef as unknown as React.Ref<HTMLDivElement>}
      initial={{ opacity: 0, x: 24 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.35, delay: index * 0.05 }}
      onClick={onClick}
      className={`group shrink-0 w-[260px] snap-start rounded-2xl bg-card cursor-pointer text-left overflow-hidden relative ${
        isActive
          ? "shadow-xl shadow-accent/15"
          : canDrop
          ? "shadow-lg shadow-accent/5"
          : "hover:shadow-lg hover:shadow-accent/5"
      }`}
      style={{
        transform: isActive ? "scale(1.04)" : canDrop ? "scale(1.01)" : "scale(1)",
        transition: "transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s ease, border-color 0.2s ease",
        borderWidth: 2,
        borderStyle: "solid",
        borderColor: isActive ? "var(--accent)" : canDrop ? "color-mix(in srgb, var(--accent) 20%, transparent)" : "transparent",
      }}
    >
      {/* Drop overlay */}
      <AnimatePresence>
        {isActive && (
          <motion.div
            initial={{ opacity: 0, backdropFilter: "blur(0px)" }}
            animate={{ opacity: 1, backdropFilter: "blur(8px)" }}
            exit={{ opacity: 0, backdropFilter: "blur(0px)" }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="absolute inset-0 z-10 bg-accent/6 flex items-center justify-center pointer-events-none"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.85, y: 6 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 4 }}
              transition={{ duration: 0.2, ease: [0.34, 1.56, 0.64, 1] }}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-card/95 backdrop-blur-xl border border-accent/40 shadow-lg"
            >
              <FolderPlus size={14} className="text-accent" />
              <span
                className="text-accent"
                style={{ fontSize: "0.78rem", fontFamily: "'Fraunces', Georgia, serif" }}
              >
                Drop to add
              </span>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Cover photo, or a solid block of the collection's color */}
      <div className="h-24 w-full overflow-hidden">
        {coverUrl ? (
          <img src={coverUrl} alt="" className="w-full h-full object-cover" />
        ) : (
          <div
            className="w-full h-full"
            style={{ backgroundColor: collection.cover_color || "var(--accent)" }}
          />
        )}
      </div>
      <div className="p-4.5">
        <div className="flex items-center gap-1.5 mb-1">
          {collection.is_public ? (
            <Globe size={11} className="text-accent/50 shrink-0" />
          ) : (
            <Lock size={11} className="text-muted-foreground/30 shrink-0" />
          )}
          <span
            className="text-muted-foreground/40 tabular-nums"
            style={{ fontSize: "0.68rem" }}
          >
            {collection.card_count} {collection.card_count === 1 ? "thought" : "thoughts"}
          </span>
        </div>
        <span
          className="text-primary block truncate"
          style={{
            fontFamily: "'Fraunces', Georgia, serif",
            fontSize: "0.88rem",
          }}
        >
          {collection.name}
        </span>
      </div>
    </motion.div>
  );
}

/* ─── Full-width Collections Carousel ─── */
/* ─── Loose Thoughts (vertical scroll masonry) ─── */
function LooseThoughtsCarousel({
  thoughts,
  onSelect,
  onDelete,
  onSave,
  onUploadImage,
  dropCardTrigger,
}: {
  thoughts: Thought[];
  onSelect: (t: Thought) => void;
  onDelete: (id: string) => void;
  onSave: (payload: { title?: string; body: string }) => void;
  onUploadImage?: (file: File, body?: string) => void;
  dropCardTrigger?: number;
}) {
  return (
    <div
      className="overflow-y-auto overflow-x-hidden rounded-xl"
      style={{ maxHeight: 340 }}
    >
      <ResponsiveMasonry columnsCountBreakPoints={{ 350: 1, 500: 2, 768: 3 }}>
        <Masonry gutter="10px">
          <DropCard onSave={onSave} onUploadImage={onUploadImage} triggerOpen={dropCardTrigger} />
          {thoughts.map((thought, i) => (
            <DraggableThought
              key={thought.id}
              thought={thought}
              index={i}
              onClick={() => onSelect(thought)}
              onDelete={onDelete}
            />
          ))}
        </Masonry>
      </ResponsiveMasonry>
    </div>
  );
}

/* ─── Full-width Collections Carousel ─── */
function CollectionsCarousel({
  collections,
  onCollectionClick,
  onDropThought,
}: {
  collections: Collection[];
  onCollectionClick: (id: string) => void;
  onDropThought: (thoughtId: string, collectionId: string) => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  }, []);

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(checkScroll);
    ro.observe(el);
    return () => ro.disconnect();
  }, [checkScroll, collections.length]);

  const scroll = (dir: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir === "left" ? -320 : 320, behavior: "smooth" });
  };

  if (collections.length === 0) return null;

  return (
    <div className="relative group/carousel">
      {/* Left chevron */}
      <AnimatePresence>
        {canScrollLeft && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.15 }}
            onClick={() => scroll("left")}
            className="absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-card/95 backdrop-blur-sm border border-border/60 shadow-lg flex items-center justify-center text-muted-foreground hover:text-accent hover:border-accent/30 transition-colors"
          >
            <ChevronLeft size={18} />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Right chevron */}
      <AnimatePresence>
        {canScrollRight && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.15 }}
            onClick={() => scroll("right")}
            className="absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-card/95 backdrop-blur-sm border border-border/60 shadow-lg flex items-center justify-center text-muted-foreground hover:text-accent hover:border-accent/30 transition-colors"
          >
            <ChevronRight size={18} />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Fade edges — wider than container to cover arrow gap */}
      <div
        className="absolute -left-5 top-0 bottom-0 z-[5] pointer-events-none transition-opacity duration-200"
        style={{ width: 80, opacity: canScrollLeft ? 1 : 0, background: "linear-gradient(to right, var(--background) 30%, var(--background) 50%, var(--background-fade))" }}
      />
      <div
        className="absolute -right-5 top-0 bottom-0 z-[5] pointer-events-none transition-opacity duration-200"
        style={{ width: 80, opacity: canScrollRight ? 1 : 0, background: "linear-gradient(to left, var(--background) 30%, var(--background) 50%, var(--background-fade))" }}
      />

      <div
        ref={scrollRef}
        onScroll={checkScroll}
        className="flex gap-3 overflow-x-auto overflow-y-hidden pb-2 -mx-1 px-1 snap-x snap-mandatory"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none", WebkitOverflowScrolling: "touch" }}
      >
        {collections.map((c, i) => (
          <DroppableCollectionCard
            key={c.id}
            collection={c}
            index={i}
            onClick={() => onCollectionClick(c.id)}
            onDrop={onDropThought}
          />
        ))}
      </div>
    </div>
  );
}

/* ─── Main Page ─── */
export function HomePage() {
  const navigate = useNavigate();
  const auth = useAuth();
  const { state, addThought, addImageThought, addToCollection, deleteThought, showToast } = useBurrow();
  const firstName =
    auth.status === "signed_in"
      ? (auth.user.user_metadata?.first_name as string | undefined) ||
        (auth.user.email?.split("@")[0] ?? "")
      : "";
  const [selectedThought, setSelectedThought] = useState<Thought | null>(null);
  const [showCollectAll, setShowCollectAll] = useState(false);
  const [dropCardTrigger, setDropCardTrigger] = useState(0);
  const [showCreateCollection, setShowCreateCollection] = useState(false);

  // Global "N" shortcut to open drop card
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "n" && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const tag = (e.target as HTMLElement).tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || (e.target as HTMLElement).isContentEditable) return;
        e.preventDefault();
        setDropCardTrigger((c) => c + 1);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const thoughts = state.thoughts;
  
  // Loose thoughts = not in a collection and not part of a trail/thread
  // (cards in a thread already have connections, so they aren't loose).
  const collectedIds = new Set(state.collections.flatMap((c) => c.card_ids));
  const looseThoughts = thoughts.filter((t) => !collectedIds.has(t.id) && !t.in_thread);

  // Cross-device Continue Thinking state (pins, cooldown, cadence) — was
  // per-browser localStorage, now synced via the resurface_state table.
  const {
    loaded: resurfaceLoaded,
    pinnedIds,
    togglePin,
    surfacedAt,
    recordSurfaced,
    frequency: resurfaceFrequency,
  } = useResurface();

  // Kept fresh synchronously every render so the memo below reads the latest
  // snapshot WITHOUT surfacedAt being a dependency — if it were, the
  // record-surfaced effect would retrigger the memo and loop/reshuffle.
  const surfacedAtRef = useRef(surfacedAt);
  surfacedAtRef.current = surfacedAt;

  // "Continue Thinking" — spaced repetition with cooldown
  // Cards you've seen recently are deprioritized; cards you haven't engaged with rise up
  const continueThinking = useMemo(() => {
    const cooldownMs = COOLDOWN_MS[resurfaceFrequency] ?? COOLDOWN_MS.daily;
    const now = Date.now();
    const surfacedMap = surfacedAtRef.current;

    const pinned = [...pinnedIds]
      .map((id) => thoughts.find((t) => t.id === id))
      .filter(Boolean) as Thought[];

    const eligible = thoughts.filter((t) =>
      !pinnedIds.has(t.id) && (t.connections > 0 || t.title)
    );

    // Score: higher = more likely to surface
    // Cards not seen recently score highest; cards seen recently score lowest
    const scored = eligible.map((t) => {
      const lastSeen = surfacedMap[t.id] ?? 0;
      const timeSince = now - lastSeen;
      const cooledOff = timeSince >= cooldownMs;

      // Age bonus: older cards that haven't been revisited get a boost
      const ageMs = now - new Date(t.created_at).getTime();
      const ageDays = ageMs / (1000 * 60 * 60 * 24);

      let score = 0;
      if (cooledOff) score += 100; // Past cooldown = eligible
      score += Math.min(ageDays, 60); // Older cards score higher, cap at 60
      score += (t.connections ?? 0) * 5; // Connected cards are more interesting
      if (t.labels?.length) score += 10; // Labeled cards slightly preferred
      score += Math.random() * 20; // Some randomness

      return { thought: t, score, cooledOff };
    });

    // If not enough cooled-off cards, reset cooldowns (allow all)
    const cooledOff = scored.filter((s) => s.cooledOff);
    const pool = cooledOff.length >= (6 - pinned.length) ? cooledOff : scored;

    const suggestions = pool
      .sort((a, b) => b.score - a.score)
      .slice(0, Math.max(0, 6 - pinned.length))
      .map((s) => s.thought);

    return [...pinned, ...suggestions];
    // surfacedAt is read via ref (snapshot) so recording a surface doesn't
    // retrigger this memo and reshuffle the list — see surfacedAtRef.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thoughts, pinnedIds, resurfaceFrequency, resurfaceLoaded]);

  // Record surfacing in an effect (never during render) so the list stays
  // stable within a visit and we don't write to the DB mid-render.
  useEffect(() => {
    if (resurfaceLoaded && continueThinking.length > 0) {
      recordSurfaced(continueThinking.map((t) => t.id));
    }
  }, [resurfaceLoaded, continueThinking, recordSurfaced]);

  const stats = [
    {
      label: "new today",
      value: thoughts.filter((t) => {
        const d = new Date(t.created_at);
        const now = new Date();
        return d.toDateString() === now.toDateString();
      }).length,
    },
    // Server-side count, not `thoughts.length` — the store only holds the
    // pages fetched so far, so the array caps out at the hydrate page size.
    { label: "total thoughts", value: state.thoughtsTotal },
    {
      label: "connections",
      // Summing the loaded cards' degrees caps with the loaded set, exactly as
      // the card count did. Use the hydrate-wide total until the whole library
      // is in memory, at which point the live sum is both exact and reactive.
      value: state.thoughtsAllLoaded
        ? thoughts.reduce((sum, t) => sum + t.connections, 0)
        : state.connectionsTotal,
    },
    { label: "collections", value: state.collections.length },
  ];

  const handleSaveThought = ({ title, body }: { title?: string; body: string }) => {
    const text = body.trim();
    const urlPattern = /^https?:\/\/[^\s]+$/;
    if (urlPattern.test(text)) {
      addThought(text, title, text, new URL(text).hostname, "home_inline");
    } else {
      addThought(text, title, undefined, undefined, "home_inline");
    }
  };

  const handleUploadImage = (file: File, body?: string) => {
    addImageThought(file, { body, source: "home_inline" });
  };

  const handleDropOnCollection = useCallback(
    (thoughtId: string, collectionId: string) => {
      const collection = state.collections.find((c) => c.id === collectionId);
      if (!collection) return;
      if (collection.card_ids.includes(thoughtId)) {
        showToast("Already in that collection.");
        return;
      }
      addToCollection(thoughtId, collectionId);
    },
    [state.collections, addToCollection, showToast]
  );

  const looseIds = looseThoughts.map((t) => t.id);

  // HTML5 drag events never fire on touch screens, so card→collection drag
  // was dead on phones. On coarse pointers use the touch backend with a
  // long-press to start (like rearranging an iOS home screen) so ordinary
  // swipes still scroll the masonry. The CustomDragLayer works with both.
  const isTouch = useMemo(
    () => typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches,
    []
  );

  return (
    <DndProvider
      backend={isTouch ? TouchBackend : HTML5Backend}
      options={isTouch ? { delayTouchStart: 250, enableMouseEvents: true } : undefined}
    >
      <CustomDragLayer thoughts={state.thoughts} />
      <PageDropZone>
      <div className="min-h-dvh bg-background">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10">
          {/* Header */}
          <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h1
                className="text-primary"
                style={{
                  fontFamily: "'Fraunces', Georgia, serif",
                  fontSize: "clamp(1.8rem, 4vw, 2.5rem)",
                  fontWeight: 400,
                }}
              >
                {getGreeting()}{firstName ? `, ${firstName}` : ""}
              </h1>
              <p className="text-muted-foreground mt-1" style={{ fontSize: "0.9rem" }}>
                Here is what's happening with your thoughts today
              </p>
            </motion.div>

            <StatsBar stats={stats} />
          </header>

          {/* ── Loose Thoughts (with inline DropCard) ── */}
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mb-8"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <h2
                  className="text-primary"
                  style={{
                    fontFamily: "'Fraunces', Georgia, serif",
                    fontSize: "1.2rem",
                  }}
                >
                  Loose Thoughts
                </h2>
                <span
                  className="px-2.5 py-0.5 rounded-full bg-accent/15 text-accent"
                  style={{ fontSize: "0.75rem" }}
                >
                  {looseThoughts.length}
                </span>
              </div>
              {looseThoughts.length > 0 && (
                <button
                  onClick={() => setShowCollectAll(true)}
                  className="text-muted-foreground hover:text-accent transition-colors flex items-center gap-1.5"
                  style={{ fontSize: "0.82rem" }}
                >
                  <FolderPlus size={14} />
                  Collect all <ArrowRight size={14} />
                </button>
              )}
            </div>

            <div className="rounded-2xl bg-accent/8 border border-accent/15 p-5">
              <LooseThoughtsCarousel
                thoughts={looseThoughts}
                onSelect={setSelectedThought}
                onDelete={deleteThought}
                onSave={handleSaveThought}
                onUploadImage={handleUploadImage}
                dropCardTrigger={dropCardTrigger}
              />
            </div>
          </motion.section>

          {/* ── Collections Carousel (full-width, droppable) ── */}
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="mb-8"
          >
            <div className="flex items-center justify-between mb-4 gap-3">
              <h2
                className="text-primary"
                style={{
                  fontFamily: "'Fraunces', Georgia, serif",
                  fontSize: "1.2rem",
                }}
              >
                My Collections
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowCreateCollection(true)}
                  className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-border/40 text-muted-foreground hover:text-foreground hover:border-accent/40 hover:bg-muted/20 transition-all"
                  style={{ fontSize: "0.8rem" }}
                >
                  <Plus size={13} />
                  New
                </button>
                <button
                  onClick={() => navigate("/collections")}
                  className="text-muted-foreground hover:text-accent transition-colors flex items-center gap-1"
                  style={{ fontSize: "0.82rem" }}
                >
                  View all <ArrowRight size={14} />
                </button>
              </div>
            </div>

            {state.hydrated ? (
              state.collections.length > 0 ? (
                <CollectionsCarousel
                  collections={state.collections}
                  onCollectionClick={(id) => navigate(`/collections/${id}`)}
                  onDropThought={handleDropOnCollection}
                />
              ) : (
                <div className="rounded-2xl border border-dashed border-border/40 bg-card/40 px-5 py-6 text-sm text-muted-foreground">
                  You don’t have any collections yet. Create one from the Library or the Collections page to start
                  grouping thoughts.
                </div>
              )
            ) : (
              <div className="flex gap-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div
                    key={i}
                    className="h-[140px] w-[220px] rounded-2xl border border-border/30 bg-muted/40 animate-pulse"
                  />
                ))}
              </div>
            )}
          </motion.section>

          {/* ── Continue Thinking (Masonry) ── */}
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.25 }}
            className="mb-8"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <h2
                  className="text-primary"
                  style={{
                    fontFamily: "'Fraunces', Georgia, serif",
                    fontSize: "1.2rem",
                  }}
                >
                  Continue Thinking
                </h2>
                <Brain size={14} className="text-accent/50" />
              </div>
              <button
                onClick={() => navigate("/thinking")}
                className="text-muted-foreground hover:text-accent transition-colors"
                style={{ fontSize: "0.82rem" }}
              >
                Thinking Space
              </button>
            </div>

            {state.hydrated ? (
              continueThinking.length > 0 ? (
                <ResponsiveMasonry columnsCountBreakPoints={{ 350: 2, 500: 3, 900: 4 }}>
                  <Masonry gutter="10px">
                    {continueThinking.map((thought, i) => (
                      <DraggableThought
                        key={thought.id}
                        thought={thought}
                        index={i}
                        onClick={() => setSelectedThought(thought)}
                        onDelete={deleteThought}
                        pinned={pinnedIds.has(thought.id)}
                        onTogglePin={() => togglePin(thought.id)}
                      />
                    ))}
                  </Masonry>
                </ResponsiveMasonry>
              ) : (
                <div className="rounded-2xl border border-dashed border-border/40 bg-card/40 px-5 py-6 text-sm text-muted-foreground">
                  Once you start connecting and titling thoughts, we’ll surface a few here as suggestions for where to
                  think next.
                </div>
              )
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div
                    key={i}
                    className="h-[180px] rounded-2xl border border-border/30 bg-muted/40 animate-pulse"
                  />
                ))}
              </div>
            )}
          </motion.section>
        </div>

        {/* Card detail side sheet */}
        <CardDetailSheet
          thought={selectedThought}
          onClose={() => setSelectedThought(null)}
        />

        {/* Collect All modal */}
        <CollectionPicker
          open={showCollectAll}
          onClose={() => setShowCollectAll(false)}
          thoughtIds={looseIds}
          title={`Collect ${looseThoughts.length} loose thoughts`}
        />

        {/* Create Collection modal */}
        <AnimatePresence>
          {showCreateCollection && (
            <Suspense fallback={null}>
              <CreateCollectionModal onClose={() => setShowCreateCollection(false)} />
            </Suspense>
          )}
        </AnimatePresence>
      </div>
      </PageDropZone>
    </DndProvider>
  );
}